import axiosInstance from '@/services/axiosInstance';
import { pickBody } from '@/api/helpers';

export type CalendarSummary = {
  id: string;
  name: string;
  color: string | null;
};

export type CalendarEvent = {
  id: string;
  organizationId: string;
  calendarId: string;
  createdById: string;
  updatedById: string | null;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  description: string | null;
  location: string | null;
  status: string;
  visibility: string;
  kind: string;
  recurrenceRule: string | null;
  recurrenceEndsAt: string | null;
  seriesMasterId: string | null;
  /** Minutes before start; `0` = at start. */
  reminderOffsetsMinutes?: number[] | null;
  createdAt: string;
  updatedAt: string;
  calendar?: CalendarSummary;
};

type ListResponse = { success?: boolean; data: CalendarEvent[] };

export type ListEventsParams = {
  calendarId?: string;
  /** ISO strings; both required by backend when filtering by range. */
  from?: string;
  to?: string;
};

const list = (params?: ListEventsParams): Promise<CalendarEvent[]> =>
  axiosInstance.get<unknown>('/api/v1/events', { params }).then((res) => {
    const raw = pickBody<ListResponse | CalendarEvent[]>(res.data);
    if (Array.isArray(raw)) return raw;
    return raw?.data ?? [];
  });

export type CreateEventInput = {
  calendarId?: string;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay?: boolean;
  description?: string | null;
  location?: string | null;
  status?: 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED';
  visibility?: 'DEFAULT' | 'PUBLIC' | 'PRIVATE';
  kind?: 'EVENT' | 'OUT_OF_OFFICE' | 'FOCUS_TIME';
  recurrenceRule?: string | null;
  recurrenceEndsAt?: string | null;
  seriesMasterId?: string | null;
  /** Omit to use server default (e.g. 15 + at start). Pass `[]` for none. */
  reminderOffsetsMinutes?: number[];
};

const create = (input: CreateEventInput): Promise<CalendarEvent> =>
  axiosInstance.post<unknown>('/api/v1/events', input).then((res) => {
    const raw = pickBody<{ success?: boolean; data: CalendarEvent }>(res.data);
    return raw.data;
  });

export type PatchEventInput = Partial<
  Pick<
    CreateEventInput,
    | 'calendarId'
    | 'title'
    | 'startsAt'
    | 'endsAt'
    | 'allDay'
    | 'description'
    | 'location'
    | 'status'
    | 'visibility'
    | 'kind'
    | 'recurrenceRule'
    | 'recurrenceEndsAt'
    | 'seriesMasterId'
    | 'reminderOffsetsMinutes'
  >
>;

const update = (id: string, input: PatchEventInput): Promise<CalendarEvent> =>
  axiosInstance.patch<unknown>(`/api/v1/events/${id}`, input).then((res) => {
    const raw = pickBody<{ success?: boolean; data: CalendarEvent }>(res.data);
    return raw.data;
  });

const eventsApi = {
  list,
  create,
  update,
};

export { eventsApi };
